// src/components/worksheets/WorksheetRunner.jsx
//
// Interactive worksheet / checklist, presented as a STEP-THROUGH: one section
// per card with Back / Next, a section rail on wide screens, and a final
// Review step (per-section progress, Talk to Someone, Mark complete). Built to
// be shown full-screen (see WorksheetOverlay) or inline on My Journal.
//
// Answers journal to the user's self-only private_journal as they type
// (debounced). "Mark complete" also completes the Guided Path checkpoint that
// sent the user here, like a finished video does.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, CheckCircle2, ChevronLeft, ChevronRight, ClipboardList, PenLine, Save, HeartHandshake, Plus, Trash2, Flag } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { auth } from '../../config/firebase-config';
import { useOrganization } from '../../context/OrganizationContext';
import { getWorksheet, progressFor } from '../../data/worksheets';
import { subscribeJournalEntry, saveJournalEntry } from '../../utils/journalStore';
import { PREVIEW_UID, activePathNodeFor, markPathNodeDone, pathNodeIdsForTopic } from '../../utils/guidedPath';
import { voiceText } from '../../utils/worksheetVoice';
import SupportOutlet from '../dashboard/SupportOutlet';

const SAVE_DEBOUNCE_MS = 700;
const CHART_COLORS = ['var(--brand-primary)', '#10b981', '#f59e0b', '#8b5cf6'];

const WorksheetRunner = ({ worksheetId, currentUser, topicId, onComplete, onPathStepDone, onClose }) => {
    const def = useMemo(() => getWorksheet(worksheetId), [worksheetId]);
    const { orgData, isPreviewMode } = useOrganization();
    const isCorporate = orgData?.industry === 'corporate';
    const t = (s) => (s ? voiceText(s, isCorporate) : s);

    const uid = isPreviewMode ? PREVIEW_UID : (currentUser?.uid || auth.currentUser?.uid || null);

    const [answers, setAnswers] = useState({});
    const [entry, setEntry] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [saveState, setSaveState] = useState('idle'); // idle | pending | saving | saved | error
    const [step, setStep] = useState(0);
    const dirtyRef = useRef(false);
    const timerRef = useRef(null);
    const pendingRef = useRef({});
    const scrollRef = useRef(null);

    const sections = useMemo(() => def?.sections || [], [def]);
    const reviewStep = sections.length;          // the last card
    const isReview = step === reviewStep;

    // Initial load (later remote snapshots are ignored while editing — single-device journal)
    useEffect(() => {
        setLoaded(false);
        dirtyRef.current = false;
        setStep(0);
        const unsub = subscribeJournalEntry(uid, worksheetId, (e) => {
            setEntry(e);
            if (!dirtyRef.current) setAnswers(e?.answers || {});
            setLoaded(true);
        });
        return () => unsub();
    }, [uid, worksheetId]);

    // Scroll the card to the top on every step change
    useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 }); }, [step]);

    const progress = useMemo(() => progressFor(def, answers), [def, answers]);
    const sectionProgress = useMemo(
        () => sections.map(s => progressFor({ sections: [s] }, answers)),
        [sections, answers]
    );

    const flush = async (nextAnswers) => {
        if (!uid || !def) return;
        const patch = pendingRef.current;
        pendingRef.current = {};
        setSaveState('saving');
        try {
            await saveJournalEntry(uid, worksheetId, {
                worksheetId, topicId: def.topicId, kind: def.kind, title: def.title,
                answers: patch,
                progress: progressFor(def, nextAnswers),
                ...(entry?.startedAt ? {} : { startedAt: new Date().toISOString() })
            });
            setSaveState('saved');
        } catch (e) {
            console.error('journal save failed', e);
            setSaveState('error');
        }
    };

    const setAnswer = (promptId, value) => {
        dirtyRef.current = true;
        setAnswers(prev => {
            const next = { ...prev, [promptId]: value };
            pendingRef.current = { ...pendingRef.current, [promptId]: value };
            setSaveState('pending');
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => flush(next), SAVE_DEBOUNCE_MS);
            return next;
        });
    };

    useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

    const completed = !!entry?.completedAt;
    const toggleComplete = async () => {
        if (!uid) return;
        const now = new Date().toISOString();
        const nextCompleted = !completed;
        try {
            await saveJournalEntry(uid, worksheetId, {
                worksheetId, topicId: def.topicId, kind: def.kind, title: def.title,
                progress, completedAt: nextCompleted ? now : null
            });
            if (nextCompleted) {
                // Tick the Guided Path checkpoint(s) for this topic — the one that sent
                // the user here if any, plus every metric/recovery node routed to it.
                const active = topicId ? activePathNodeFor(topicId) : null;
                const ids = new Set([...(active ? [active.id] : []), ...pathNodeIdsForTopic(def.topicId || topicId)]);
                for (const id of ids) {
                    try { await markPathNodeDone(uid, id); } catch (e) { /* non-fatal */ }
                }
                if (ids.size > 0 && onPathStepDone) onPathStepDone();
                if (onComplete) onComplete();
            }
        } catch (e) {
            console.error('complete failed', e);
        }
    };

    if (!def) return <div className="p-8 text-center text-slate-400 text-sm">This worksheet isn't available.</div>;
    if (!uid) return <div className="p-8 text-center text-slate-500 text-sm">Sign in to use this worksheet — your answers are private to you.</div>;

    const supportFlagged = sections.some(s => (s.prompts || []).some(p =>
        p.type === 'multi' && (p.options || []).some(o => o.flag === 'support' && answers[p.id]?.[o.id])
    ));
    const Icon = def.kind === 'checklist' ? ClipboardList : PenLine;
    const current = sections[step];
    const saveLabel = !loaded ? 'Loading…'
        : (saveState === 'saving' || saveState === 'pending') ? 'Saving…'
            : saveState === 'error' ? 'Save failed — check your connection'
                : entry?.updatedAt ? `Saved ${new Date(entry.updatedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Saves as you go';

    return (
        <div className="flex flex-col h-full min-h-0 bg-slate-50">
            {/* ===== TOP BAR ===== */}
            <div className="shrink-0 bg-white border-b border-slate-200 px-4 md:px-6 py-3">
                <div className="max-w-5xl mx-auto flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-brand-light text-brand-primary flex items-center justify-center shrink-0"><Icon size={20} /></div>
                    <div className="min-w-0 flex-1">
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 truncate">{def.kind === 'checklist' ? 'Checklist' : 'Worksheet'} · private to you</p>
                        <h2 className="text-base md:text-lg font-black text-slate-900 leading-tight line-clamp-2 md:truncate">{t(def.title)}</h2>
                    </div>
                    <div className="text-right shrink-0">
                        <p className="text-xl font-black text-brand-primary leading-none">{progress.pct}%</p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-0.5">{isReview ? 'Review' : `Step ${step + 1} of ${sections.length}`}</p>
                    </div>
                    {onClose && (
                        <button onClick={onClose} className="ml-1 px-3 py-2 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-100 shrink-0">Close</button>
                    )}
                </div>
                {/* step dots */}
                <div className="max-w-5xl mx-auto flex items-center gap-1.5 mt-3">
                    {sections.map((s, i) => {
                        const sp = sectionProgress[i];
                        const state = i === step ? 'current' : sp.pct === 100 ? 'done' : sp.answered > 0 ? 'partial' : 'todo';
                        return (
                            <button key={s.id} onClick={() => setStep(i)} title={t(s.title)} aria-label={`Go to ${t(s.title)}`}
                                className={`h-1.5 flex-1 rounded-full transition-colors ${state === 'current' ? 'bg-brand-primary' : state === 'done' ? 'bg-emerald-400' : state === 'partial' ? 'bg-brand-primary/40' : 'bg-slate-200 hover:bg-slate-300'}`} />
                        );
                    })}
                    <button onClick={() => setStep(reviewStep)} title="Review" aria-label="Go to review"
                        className={`h-1.5 w-6 rounded-full transition-colors ${isReview ? 'bg-brand-primary' : completed ? 'bg-emerald-400' : 'bg-slate-200 hover:bg-slate-300'}`} />
                </div>
            </div>

            {/* ===== BODY ===== */}
            <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
                <div className="max-w-5xl mx-auto p-4 md:p-6 md:grid md:grid-cols-[220px_1fr] md:gap-6">
                    {/* section rail (wide screens) */}
                    <aside className="hidden md:block">
                        <div className="sticky top-2 space-y-1">
                            {sections.map((s, i) => {
                                const sp = sectionProgress[i];
                                return (
                                    <button key={s.id} onClick={() => setStep(i)}
                                        className={`w-full text-left px-3 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors ${i === step ? 'bg-brand-primary text-white' : 'text-slate-600 hover:bg-white'}`}>
                                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 ${i === step ? 'bg-white/20 text-white' : sp.pct === 100 ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                                            {sp.pct === 100 && i !== step ? <Check size={11} strokeWidth={4} /> : i + 1}
                                        </span>
                                        <span className="truncate">{t(s.title)}</span>
                                    </button>
                                );
                            })}
                            <button onClick={() => setStep(reviewStep)}
                                className={`w-full text-left px-3 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors ${isReview ? 'bg-brand-primary text-white' : 'text-slate-600 hover:bg-white'}`}>
                                <span className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${isReview ? 'bg-white/20 text-white' : completed ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}><Flag size={11} /></span>
                                Review & complete
                            </button>
                        </div>
                    </aside>

                    {/* THE CARD */}
                    <div className="min-w-0">
                        {!isReview && current && (
                            <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-7 animate-in fade-in slide-in-from-right-2 duration-300" key={current.id}>
                                <p className="text-[10px] font-black uppercase tracking-widest text-brand-primary mb-1">Step {step + 1} of {sections.length}</p>
                                <h3 className="text-xl md:text-2xl font-black text-slate-900 leading-tight">{t(current.title)}</h3>
                                {step === 0 && def.intro && <p className="text-sm text-slate-500 font-medium mt-2">{t(def.intro)}</p>}
                                {current.description && <p className="text-sm text-slate-500 font-medium mt-2">{t(current.description)}</p>}
                                <div className="space-y-6 mt-6">
                                    {(current.prompts || []).map(p => (
                                        <Prompt key={p.id} prompt={p} value={answers[p.id]} onChange={(v) => setAnswer(p.id, v)} t={t} />
                                    ))}
                                </div>
                            </div>
                        )}

                        {isReview && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-right-2 duration-300">
                                <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-5 md:p-7">
                                    <p className="text-[10px] font-black uppercase tracking-widest text-brand-primary mb-1">Review</p>
                                    <h3 className="text-xl md:text-2xl font-black text-slate-900 leading-tight">{completed ? 'Completed' : 'Where you are'}</h3>
                                    <p className="text-sm text-slate-500 font-medium mt-2">{t(def.outro || '')}</p>
                                    <div className="mt-5 space-y-2">
                                        {sections.map((s, i) => {
                                            const sp = sectionProgress[i];
                                            return (
                                                <button key={s.id} onClick={() => setStep(i)} className="w-full flex items-center gap-3 p-3 rounded-xl bg-slate-50 hover:bg-slate-100 text-left transition-colors">
                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-black shrink-0 ${sp.pct === 100 ? 'bg-emerald-500 text-white' : sp.answered > 0 ? 'bg-brand-primary text-white' : 'bg-slate-200 text-slate-500'}`}>
                                                        {sp.pct === 100 ? <Check size={12} strokeWidth={4} /> : i + 1}
                                                    </span>
                                                    <span className="flex-1 text-sm font-bold text-slate-800 truncate">{t(s.title)}</span>
                                                    <span className="text-xs font-bold text-slate-500 shrink-0">{sp.answered}/{sp.total}</span>
                                                    <ChevronRight size={16} className="text-slate-300 shrink-0" />
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                {(supportFlagged || def.supportNote) && (
                                    <div className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${supportFlagged ? 'bg-rose-50 border-rose-200' : 'bg-white border-slate-200'}`}>
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${supportFlagged ? 'bg-rose-100 text-rose-600' : 'bg-brand-light text-brand-primary'}`}><HeartHandshake size={20} /></div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-black text-slate-900">{supportFlagged ? "You don't have to handle this on your own." : 'Want to talk it through with someone?'}</p>
                                            <p className="text-xs text-slate-600 font-medium mt-0.5">{supportFlagged ? 'Reaching out now is a strong move, not a weak one.' : t(def.supportNote || '')}</p>
                                        </div>
                                        <SupportOutlet currentUser={currentUser} topicId={def.topicId} className={`px-4 py-2.5 rounded-full font-bold text-sm flex items-center justify-center gap-2 shrink-0 transition-colors ${supportFlagged ? 'bg-rose-600 hover:bg-rose-700 text-white' : 'bg-brand-primary hover:bg-brand-dark text-white'}`} />
                                    </div>
                                )}

                                <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                    <p className="text-xs text-slate-500 font-medium">{completed ? 'Marked complete. You can keep editing — it saves as you go.' : 'Done for now? Marking it complete also ticks it off your Guided Path.'}</p>
                                    <button onClick={toggleComplete} className={`px-5 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 shrink-0 transition-colors ${completed ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-brand-primary hover:bg-brand-dark text-white'}`}>
                                        {completed ? <CheckCircle2 size={16} /> : <Check size={16} strokeWidth={3} />} {completed ? 'Completed — undo' : 'Mark complete'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ===== FOOTER: Back / Next ===== */}
            <div className="shrink-0 bg-white border-t border-slate-200 px-4 md:px-6 py-3">
                <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
                    <span className={`text-[11px] font-bold flex items-center gap-1 min-w-0 truncate ${saveState === 'error' ? 'text-red-500' : 'text-slate-400'}`}><Save size={12} className="shrink-0" /> {saveLabel}</span>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setStep(s => Math.max(0, s - 1))} disabled={step === 0}
                            className="px-4 py-2.5 rounded-xl font-bold text-sm border border-slate-200 text-slate-600 hover:border-slate-300 disabled:opacity-40 flex items-center gap-1">
                            <ChevronLeft size={16} /> Back
                        </button>
                        {!isReview ? (
                            <button onClick={() => setStep(s => Math.min(reviewStep, s + 1))}
                                className="px-5 py-2.5 rounded-xl font-bold text-sm bg-brand-primary hover:bg-brand-dark text-white flex items-center gap-1">
                                {step === sections.length - 1 ? 'Review' : 'Next'} <ChevronRight size={16} />
                            </button>
                        ) : (
                            onClose ? (
                                <button onClick={onClose} className="px-5 py-2.5 rounded-xl font-bold text-sm bg-slate-800 hover:bg-slate-900 text-white">Done</button>
                            ) : null
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

// Full-screen container used from topic pages (own close button, phone-friendly)
export const WorksheetOverlay = ({ onClose, ...props }) => (
    <div className="fixed inset-0 z-[210] bg-slate-50 animate-in fade-in duration-200">
        <WorksheetRunner {...props} onClose={onClose} />
    </div>
);

// ---------------------------------------------------------------------------
// Prompt renderers
// ---------------------------------------------------------------------------
const Label = ({ prompt, t }) => (
    <div className="mb-2">
        <p className="text-sm font-black text-slate-800">{t(prompt.label)}</p>
        {prompt.help && <p className="text-xs text-slate-500 font-medium mt-0.5">{t(prompt.help)}</p>}
    </div>
);

const inputCls = 'w-full p-3 border border-slate-200 rounded-xl text-base md:text-sm outline-none focus:ring-2 focus:ring-brand-primary bg-white';

const Prompt = ({ prompt, value, onChange, t }) => {
    switch (prompt.type) {
        case 'checklist': {
            const v = value || {};
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    <div className="space-y-1.5">
                        {(prompt.items || []).map(it => {
                            const on = !!v[it.id];
                            return (
                                <button key={it.id} type="button" onClick={() => onChange({ ...v, [it.id]: !on })}
                                    className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${on ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-100 hover:border-slate-300'}`}>
                                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${on ? 'bg-emerald-500 border-emerald-500 text-white' : 'border-slate-300 bg-white'}`}>{on && <Check size={12} strokeWidth={4} />}</span>
                                    <span className="min-w-0">
                                        <span className={`block text-sm font-medium ${on ? 'text-emerald-900 line-through decoration-emerald-300' : 'text-slate-700'}`}>{t(it.label)}</span>
                                        {it.detail && <span className="block text-xs text-slate-500 font-medium mt-1 leading-relaxed">{t(it.detail)}</span>}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }
        case 'scale': {
            const min = prompt.min ?? 1, max = prompt.max ?? 10;
            const nums = Array.from({ length: max - min + 1 }, (_, i) => min + i);
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5">
                        {nums.map(n => (
                            <button key={n} type="button" onClick={() => onChange(n)}
                                className={`h-11 sm:h-10 rounded-xl text-sm font-black border transition-colors ${value === n ? 'bg-brand-primary border-brand-primary text-white' : 'bg-white border-slate-200 text-slate-600 hover:border-brand-primary/50'}`}>{n}</button>
                        ))}
                    </div>
                    {(prompt.minLabel || prompt.maxLabel) && (
                        <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-wider mt-1.5"><span>{min} · {t(prompt.minLabel)}</span><span>{max} · {t(prompt.maxLabel)}</span></div>
                    )}
                </div>
            );
        }
        case 'text':
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    {prompt.multiline
                        ? <textarea className={`${inputCls} min-h-[96px]`} value={value || ''} onChange={e => onChange(e.target.value)} />
                        : <input className={inputCls} value={value || ''} onChange={e => onChange(e.target.value)} />}
                </div>
            );
        case 'list': {
            const count = prompt.count || 3;
            const arr = Array.from({ length: count }, (_, i) => (Array.isArray(value) ? value[i] : '') || '');
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    <div className="space-y-2">
                        {arr.map((v, i) => {
                            const rowLabel = Array.isArray(prompt.itemLabels) ? prompt.itemLabels[i] : null;
                            return (
                                <div key={i} className={rowLabel ? 'flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2' : 'flex items-center gap-2'}>
                                    {rowLabel
                                        ? <span className="text-[11px] font-black uppercase tracking-wider text-slate-500 sm:w-40 shrink-0 truncate" title={t(rowLabel)}>{t(rowLabel)}</span>
                                        : <span className="w-6 h-6 rounded-full bg-brand-light text-brand-primary text-[11px] font-black flex items-center justify-center shrink-0">{i + 1}</span>}
                                    <input className={inputCls} value={v} onChange={e => { const next = [...arr]; next[i] = e.target.value; onChange(next); }} />
                                </div>
                            );
                        })}
                    </div>
                </div>
            );
        }
        case 'multi': {
            const v = value || {};
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    <div className="space-y-1.5">
                        {(prompt.options || []).map(o => {
                            const on = !!v[o.id];
                            return (
                                <button key={o.id} type="button" onClick={() => onChange({ ...v, [o.id]: !on })}
                                    className={`w-full text-left flex items-start gap-3 p-3 rounded-xl border transition-colors ${on ? (o.flag ? 'bg-rose-50 border-rose-200' : 'bg-brand-light border-brand-primary/40') : 'bg-slate-50 border-slate-100 hover:border-slate-300'}`}>
                                    <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 mt-0.5 ${on ? 'bg-brand-primary border-brand-primary text-white' : 'border-slate-300 bg-white'}`}>{on && <Check size={12} strokeWidth={4} />}</span>
                                    <span className="text-sm font-medium text-slate-700">{t(o.label)}</span>
                                </button>
                            );
                        })}
                        {prompt.other && <input className={inputCls} placeholder="Other (please specify)" value={v.other || ''} onChange={e => onChange({ ...v, other: e.target.value })} />}
                    </div>
                </div>
            );
        }
        case 'contacts': {
            const count = prompt.count || 2;
            const fields = prompt.fields || ['name', 'phone'];
            const rows = Array.from({ length: count }, (_, i) => (Array.isArray(value) ? value[i] : null) || {});
            return (
                <div>
                    <Label prompt={prompt} t={t} />
                    <div className="space-y-3">
                        {rows.map((row, i) => (
                            <div key={i} className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-3 rounded-xl bg-slate-50 border border-slate-100">
                                {fields.map(f => (
                                    <input key={f} className={inputCls} placeholder={f.charAt(0).toUpperCase() + f.slice(1)} value={row[f] || ''}
                                        onChange={e => { const next = rows.map(r => ({ ...r })); next[i][f] = e.target.value; onChange(next); }} />
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            );
        }
        case 'table': {
            const rows = Array.isArray(value) ? value : [];
            const cols = prompt.columns || [];
            const maxRows = prompt.maxRows || 31;
            const setCell = (ri, cid, val) => { const next = rows.map(r => ({ ...r })); next[ri] = { ...(next[ri] || {}), [cid]: val }; onChange(next); };
            const addRow = () => { if (rows.length >= maxRows) return; const today = new Date().toISOString().slice(0, 10); onChange([...rows, { [cols[0]?.id || 'day']: cols[0]?.kind === 'date' ? today : '' }]); };
            const removeRow = (ri) => onChange(rows.filter((_, i) => i !== ri));
            const parse = (c, raw) => (c.kind === 'number' ? (raw === '' ? '' : Number(raw)) : raw);
            const chartData = prompt.chart
                ? rows.filter(r => prompt.chart.series.some(s => r[s.id] !== undefined && r[s.id] !== '')).map(r => ({
                    x: r[prompt.chart.x] || '',
                    ...Object.fromEntries(prompt.chart.series.map(s => [s.id, Number(r[s.id])]))
                }))
                : [];
            const cellInput = (r, ri, c, extra = '') => (
                <input
                    type={c.kind === 'date' ? 'date' : c.kind === 'number' ? 'number' : 'text'}
                    min={c.min} max={c.max} inputMode={c.kind === 'number' ? 'numeric' : undefined}
                    className={`${inputCls} ${extra}`}
                    value={r[c.id] ?? ''}
                    onChange={e => setCell(ri, c.id, parse(c, e.target.value))}
                />
            );
            return (
                <div>
                    <Label prompt={prompt} t={t} />

                    {rows.length === 0 && <p className="text-xs text-slate-400 font-medium mb-2">No days logged yet.</p>}

                    {/* phones: one card per day */}
                    <div className="md:hidden space-y-3">
                        {rows.map((r, ri) => (
                            <div key={ri} className="p-3 rounded-2xl bg-slate-50 border border-slate-100 space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Day {ri + 1}</span>
                                    <button type="button" onClick={() => removeRow(ri)} className="p-1.5 text-slate-300 hover:text-red-500" title="Remove"><Trash2 size={14} /></button>
                                </div>
                                {cols.map(c => (
                                    <div key={c.id}>
                                        <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{t(c.label)}</label>
                                        {cellInput(r, ri, c)}
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    {/* wide screens: the table */}
                    <div className="hidden md:block overflow-x-auto -mx-1">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr>{cols.map(c => <th key={c.id} className="text-left text-[10px] font-black uppercase tracking-wider text-slate-400 px-1 pb-1 whitespace-nowrap">{t(c.label)}</th>)}<th /></tr>
                            </thead>
                            <tbody>
                                {rows.map((r, ri) => (
                                    <tr key={ri}>
                                        {cols.map(c => (
                                            <td key={c.id} className="px-1 py-0.5">
                                                {cellInput(r, ri, c, c.kind === 'number' ? 'w-20' : c.kind === 'date' ? 'w-40' : 'min-w-[160px]')}
                                            </td>
                                        ))}
                                        <td className="px-1"><button type="button" onClick={() => removeRow(ri)} className="p-1.5 text-slate-300 hover:text-red-500" title="Remove row"><Trash2 size={14} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <button type="button" onClick={addRow} disabled={rows.length >= maxRows} className="mt-3 px-4 py-2.5 text-sm font-bold rounded-xl border border-slate-200 text-slate-600 hover:border-brand-primary/50 hover:text-brand-primary flex items-center gap-1.5 disabled:opacity-40">
                        <Plus size={16} /> Add a day
                    </button>

                    {chartData.length >= 2 && (
                        <div className="h-52 mt-4 bg-white rounded-2xl border border-slate-100 p-2">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={chartData} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                    <XAxis dataKey="x" tick={{ fontSize: 10 }} />
                                    <YAxis domain={[0, 'auto']} tick={{ fontSize: 10 }} allowDecimals={false} />
                                    <Tooltip />
                                    <Legend wrapperStyle={{ fontSize: 11 }} />
                                    {prompt.chart.series.map((s, i) => (
                                        <Line key={s.id} type="monotone" dataKey={s.id} name={t(s.label)} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                                    ))}
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    )}
                </div>
            );
        }
        default:
            return null;
    }
};

export default WorksheetRunner;
